/**
 * The page-header channel: how a routed view tells the masthead what it is.
 *
 * AppHeader is a SIBLING of <Routes> in the shell, so a view cannot hand it a
 * title by prop. The alternative — a route→title table living inside the header —
 * was rejected because it puts the name of a screen somewhere other than the
 * screen, and it cannot express a title that depends on state (a wallet label, a
 * send step). So each view registers its own header on mount and withdraws it on
 * unmount, and the masthead renders whatever is currently registered.
 *
 * THIS MODULE HOLDS THE CHANNEL, NOT THE PROVIDER. The provider is a component and
 * lives in components/PageHeaderProvider; a module that exports both a component and
 * hooks defeats Vite's fast refresh, which can only hot-swap a module whose exports
 * are all components.
 *
 * WHEN NOTHING IS REGISTERED the masthead falls back to the brand lockup. That is
 * the dashboard's state: home has no "up", and the product name belongs on the
 * screen the user lands on.
 *
 * BACK GOES TO HISTORY, not a fixed route — the same reasoning MenuPage already
 * documents. A screen can be reached from the bottom nav, the menu drawer, or a
 * deep link, so there is no single parent to send everyone to. A view that has a
 * genuine parent can override with `backTo`.
 */

import { createContext, useContext, useEffect, useRef } from 'react';

/** What a view declares about the bar while it is mounted. */
export interface PageHeaderState {
  /** The page heading, shown in place of the brand lockup. */
  title: string;
  /** Show the back control on the leading edge. Defaults to true. */
  back?: boolean;
  /** Navigate to this route instead of popping history. */
  backTo?: string;
  /**
   * Run this instead of navigating. For the multi-step flows, where back means
   * the previous STEP of the same route — Send's amount form returns to the asset
   * list, and popping history there would leave the flow entirely.
   */
  onBack?: () => void;
  /**
   * Accessible name for the back control, when "Back" is not the honest one. Send's
   * amount form goes back to the ASSET LIST, not to the previous screen, and a
   * screen-reader user is owed that distinction. Defaults to common.back.
   */
  backLabel?: string;
}

export interface PageHeaderChannel {
  /** The header the mounted view registered, or null for the brand lockup. */
  header: PageHeaderState | null;
  /** Register (or clear, with null) the current view's header. */
  setHeader: (next: PageHeaderState | null) => void;
}

export const PageHeaderContext = createContext<PageHeaderChannel | null>(null);

/** Read the registered header. For the masthead only. */
export function useRegisteredPageHeader(): PageHeaderState | null {
  return useContext(PageHeaderContext)?.header ?? null;
}

/**
 * Declare this view's masthead title and back control.
 *
 * The effect deps are the primitive fields rather than the object, so a caller
 * may pass an inline literal without re-registering on every render. On unmount
 * the header is withdrawn, which returns the bar to the brand lockup; React runs
 * the outgoing view's cleanup before the incoming view's effect, so a route change
 * never clears the header the next screen just set.
 *
 * @param state The title, and whether a back control belongs on this screen.
 */
export function usePageHeader(state: PageHeaderState): void {
  const channel = useContext(PageHeaderContext);
  const setHeader = channel?.setHeader;
  const { title, back = true, backTo, onBack, backLabel } = state;

  // onBack is almost never memoized at the call site — it is usually an inline
  // arrow — so it CANNOT be an effect dep: a new function every render would
  // re-register, which sets state, which renders again, forever. The ref holds the
  // latest closure and the effect registers a stable wrapper around it, so the
  // handler is always current while the registration is not churned. Only whether
  // a handler exists at all can flip the bar, so that boolean is the dep.
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const hasOnBack = Boolean(onBack);

  useEffect(() => {
    if (!setHeader) return;
    setHeader({
      title,
      back,
      backTo,
      backLabel,
      onBack: hasOnBack ? () => onBackRef.current?.() : undefined,
    });
    return () => setHeader(null);
  }, [setHeader, title, back, backTo, backLabel, hasOnBack]);
}
